import React from "react"
import { useDispatch } from "react-redux"
import Head from "next/head"
import { VerticalTimeline } from "react-vertical-timeline-component"

import { Button, Container, Card, Col, Row } from "react-bootstrap"

import {
    toggleMakeEnquiryModal,
    setMakeEnquiryReqType,
} from "@/slices/makeEnquiryModalSlice"
import { toggleValuationModal } from "@/slices/valuationModalSlice"

import MiscHeroImage from "@/main/miscHeroImage"
import CharacteristicsCarousel from "@/homeOwners/characteristicsCarousel"
import CharacteristicsStrip from "@/homeOwners/characteristicsStrip"
import TimelineElement from "@/homeOwners/timelineElement"
import InterestedCard from "@/homeOwners/interestedCard"
import ReviewCard from "@/homeOwners/reviewCard"

function LandlordsScreen() {
    const dispatch = useDispatch()

    const url = "https://webbjenkins.co.uk/landlords/"
    const title = "Let Your Property on the Isle of Wight - Webb Jenkins"
    const description =
        "Start earning passive income from your property. We walk you through the entire letting process, from finding a tenant to maintaining the property."
    const imgUrl =
        "https://webb-jenkins-static-164679926.imgix.net/socials/webbjenkins-letting-social.jpg"
    const imgWidth = 1200
    const imgHeight = 630
    const imgAlt = "Webb Jenkins logo with letting your property caption"

    const description1 =
        "A local independent estate agency since 1998, our knowledge of the local area is unrivalled"
    const description2 =
        "Guiding you through the complete letting process, from finding tenants to inspections and maintenance"
    const description3 =
        "Active members of relevant bodies such as The Property Ombudsman Scheme and DPS, giving you peace of mind"
    const description4 =
        "Updating you at every step of the process so that you feel completely included and informed"

    return (
        <>
            <Head>
                <link rel="canonical" href={url} />
                <title>{title}</title>
                <meta name="description" content={description} />

                <meta property="fb:app_id" content="362437448786240" />
                <meta property="og:title" content={title} />
                <meta property="og:image:url" content={imgUrl} />
                <meta property="og:image:width" content={imgWidth} />
                <meta property="og:image:height" content={imgHeight} />
                <meta property="og:image:alt" content={imgAlt} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={url} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="en" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image:src" content={imgUrl} />
                <meta name="twitter:site" content="@WebbJenkinsIOW" />
            </Head>
            <MiscHeroImage
                title="Letting your property"
                subtitle="Let your property the simple way. We handle all aspects of the letting process so that you can relax knowing it's all being taken care of"
                image="landlords-relaxing.jpg"
                alt="Couple of landlords relaxing and smiling while checking their property online"
                position="center"
                buttonText="Get a free valuation"
                buttonOnClick={() => dispatch(toggleValuationModal(true))}
            />

            <Container fluid="md" as={Card} className="card-overlap-2 p-0">
                <Card.Body className="px-0">
                    <Card.Title>
                        <h2 className="text-center">
                            What you can expect from us
                        </h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" as={Card} className="d-sm-none mt-4 p-0">
                <Card.Body className="px-0">
                    <CharacteristicsCarousel
                        description1={description1}
                        description2={description2}
                        description3={description3}
                        description4={description4}
                    />
                </Card.Body>
            </Container>

            <CharacteristicsStrip
                description1={description1}
                description2={description2}
                description3={description3}
                description4={description4}
            />

            <Container fluid="md" className=" mt-4">
                <Row>
                    <Col className="text-center">
                        <Button
                            size="lg"
                            variant="warning"
                            onClick={() => {
                                dispatch(toggleMakeEnquiryModal(true))
                                dispatch(setMakeEnquiryReqType("general"))
                            }}
                            className="px-4 rounded-0"
                        >
                            <span className="text-bold text-white">
                                Make enquiry
                            </span>
                        </Button>
                    </Col>
                </Row>
            </Container>

            <Container fluid="md" as={Card} className="p-0 mt-5">
                <Card.Body>
                    <Card.Title>
                        <h2 className="text-center">The rental process</h2>
                    </Card.Title>
                </Card.Body>
            </Container>

            <Container fluid="md" className="mt-4">
                <VerticalTimeline
                    animate={false}
                    layout={"1-column-left"}
                    className="m-0 w-100 mw-100"
                >
                    <TimelineElement
                        position="left"
                        sequence={1}
                        image="landlords/rental-valuation.jpg"
                        alt="Prospective landlord having their property valued"
                        title="Arrange a property appraisal"
                        description="To have your rental property appraised and valued ready for renting, contact us to arrange a property visit. This will be an easy and informal process where we can discuss the rental potential of your property."
                    />

                    <TimelineElement
                        position="right"
                        sequence={2}
                        image="landlords/tenant-viewing.jpg"
                        alt="Family being shown around the rental property"
                        title="Viewings"
                        description="All viewings will be accompanied by one of our professional team. We will be sure to return with valuable feedback if a suitable applicant does not wish to proceed with a tenancy."
                    />

                    <TimelineElement
                        position="left"
                        sequence={3}
                        image="landlords/select-tenant-applicant.jpg"
                        alt="Landlord being advised of suitable tenants"
                        title="Advise of suitable tenant"
                        description="Once a tenant has been found that meets with your requirements, we will properly reference them. This is to ensure suitability, take and lodge deposits, and provide all necessary paperwork and documentation to comply with the most up to date regulations and requirements."
                    />

                    <TimelineElement
                        position="right"
                        sequence={"4"}
                        image="landlords/moving-tenant-in.jpg"
                        alt="Couple thrilled to move into the rented property"
                        title="Moving the tenant in"
                        description="Prior to the tenant moving in we will ensure that all legal requirements have been met to include EICR report, gas safety checks and that the deposit is lodged correctly. At the landlords discretion we will arrange for a property inventory detailing the condition of the property that will be used for officially checking the tenant in to the property."
                    />

                    <TimelineElement
                        position="left"
                        sequence={"5"}
                        image="landlords/property-management.jpg"
                        alt="Agent holding the key used to manage the property on behalf of the landlord"
                        title="Property management"
                        description="We will manage the property on your behalf, handling all tenant issues and enquiries, collecting rent and paying it to your designated bank account. Any maintenance issues will be discussed with you prior to authorising repairs."
                    />
                </VerticalTimeline>
            </Container>
            <Container fluid="md">
                <Row className="justify-content-between d-flex">
                    <InterestedCard
                        title="Ready to let your property?"
                        buttonTitle="Get a free valuation"
                    />
                    <ReviewCard
                        review="For a person that had managed my own rental properties for a number of years, what a breath of fresh air it was handing over my rental properties to be managed by Webb Jenkins. I can now relax knowing that I’m in safe hands and that I am complying with all the necessary regulations & requirements. I can also live stress free knowing that the tenants are being well looked after."
                        reviewer="Jan & Tony"
                    />
                </Row>
            </Container>
        </>
    )
}

export default LandlordsScreen
